/**
 * WhatsApp-style floating panel for internal (employee) conversations.
 * Opens from the header envelope — list, thread reply, and new chat.
 * LTR: bottom-right. RTL: bottom-left.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Fade,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import MicIcon from "@mui/icons-material/Mic";
import RemoveIcon from "@mui/icons-material/Remove";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import ChatIcon from "@mui/icons-material/Chat";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { useUserPermission } from "@/hooks/use-permission";
import { useUserProfile } from "@/hooks/use-user-profile";
import { Permissions } from "@/components/CheckPermission/enum";
import { FONT_FAMILY } from "@/utils/font-family";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { buildRoutePath, RouteBuilder } from "@/widgets/frontdesk/routing";
import {
  internalChatPopupConversationIdAtom,
  internalChatPopupMinimizedAtom,
  internalChatPopupOpenAtom,
} from "@/store/frontdesk/atoms/internal-chat-popup.atoms";
import { directMessagesService } from "@/services/frontdesk/api/direct-messages.service";
import type {
  Conversation,
  ConversationMember,
  DMMessage,
} from "@/widgets/frontdesk/providers/internal/types/direct-messages.types";
import { InternalEmployeePicker } from "@/widgets/frontdesk/providers/chatwoot/components/compose/InternalEmployeePicker";
import {
  InternalReplyComposer,
  type InternalPendingAttachment,
} from "@/widgets/frontdesk/providers/chatwoot/components/InternalReplyComposer";
import {
  getInternalMessageAttachments,
  isInternalMediaPlaceholder,
  InternalMessageAttachments,
} from "@/widgets/frontdesk/providers/chatwoot/components/InternalMessageAttachments";
import {
  getQuoteApprovalMeta,
  getQuoteApprovalResultMeta,
  QuoteApprovalMessageCard,
  QuoteApprovalResultMessage,
} from "@/widgets/frontdesk/providers/chatwoot/components/QuoteApprovalMessageCard";
import { colorTokens } from "@/widgets/frontdesk/design-system";
import { memberDisplayName } from "@/widgets/frontdesk/providers/chatwoot/utils/internal-conversation.utils";
import {
  formatInternalChatPreview,
  localizeQuoteApprovalConversationTitle,
} from "@/widgets/quote-new/buttons-container/build-quote-approval-message";

type PopupView = "list" | "thread" | "compose";

const PANEL_WIDTH = 380;
const PANEL_HEIGHT = 560;
const BRAND = "var(--theme-primary-color, #2E3092)";
const SELECTED_ROW_BG = "#E8EDFF";
const OUTGOING_BUBBLE = "#DCE4FF";
const CHAT_PATTERN_BG = `
  radial-gradient(circle at 12% 18%, rgba(46,48,146,0.05) 0 1.5px, transparent 2px),
  radial-gradient(circle at 72% 42%, rgba(46,48,146,0.04) 0 1.5px, transparent 2px),
  radial-gradient(circle at 40% 78%, rgba(46,48,146,0.045) 0 1.5px, transparent 2px),
  #F4F5F9
`;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

function conversationTitle(
  conversation: Conversation,
  currentUserId: string | undefined,
  t?: TranslateFn,
): string {
  const others =
    conversation.members?.filter((m) => m.userId !== currentUserId) || [];
  const memberNames = others
    .map(memberDisplayName)
    .filter(Boolean)
    .join(", ");
  const named = conversation.name?.trim() || "";

  if (t) {
    const localizedQuoteTitle = localizeQuoteApprovalConversationTitle(
      named,
      t,
      conversation.contextType,
    );
    if (localizedQuoteTitle) return localizedQuoteTitle;
  }

  // If stored name is only initials (legacy), prefer full member names
  const looksLikeInitialsOnly =
    Boolean(named) &&
    named
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean)
      .every((p) => /^[A-Za-zÀ-ÿ]{1,3}$/.test(p));

  if (named && !looksLikeInitialsOnly) return named;
  if (memberNames) return memberNames;
  if (named) return named;
  if (conversation.members?.[0]) {
    return memberDisplayName(conversation.members[0]);
  }
  return "Chat";
}

/** Avatar should reflect the other person, not the conversation/quote title. */
function conversationAvatarName(
  conversation: Conversation,
  currentUserId: string | undefined,
  t?: TranslateFn,
): string {
  const others =
    conversation.members?.filter((m) => m.userId !== currentUserId) || [];
  const person = others[0];
  if (person) {
    return memberDisplayName(person);
  }
  return conversationTitle(conversation, currentUserId, t);
}

function formatListTime(timestamp?: string | null): string {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const diffH = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffH < 24) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  if (diffH < 168) {
    return date.toLocaleDateString([], { weekday: "short" });
  }
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

function formatMsgTime(timestamp?: string): string {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDayLabel(timestamp?: string): string {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function dayKey(timestamp?: string): string {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function isVoicePreview(content?: string | null): boolean {
  const text = String(content || "").toLowerCase();
  return text.includes("voice message") || text.includes("🎤");
}

export const InternalChatPopup: React.FC = () => {
  const { t } = useTranslation();
  const dir: "rtl" | "ltr" =
    t("direction") === "rtl" ||
    (typeof document !== "undefined" &&
      document.documentElement.getAttribute("dir") === "rtl")
      ? "rtl"
      : "ltr";
  const isRTL = dir === "rtl";
  const { neutralColor } = useGomakeTheme();
  const { CheckPermission } = useUserPermission();
  const canShow = CheckPermission(Permissions.SHOW_FRONT_DESK);
  const { profileState } = useUserProfile();
  const currentUserId =
    profileState?.userId || profileState?.id || undefined;

  const [open, setOpen] = useRecoilState(internalChatPopupOpenAtom);
  const [minimized, setMinimized] = useRecoilState(
    internalChatPopupMinimizedAtom,
  );
  const [focusConversationId, setFocusConversationId] = useRecoilState(
    internalChatPopupConversationIdAtom,
  );

  const [view, setView] = useState<PopupView>("list");
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const [activeConversation, setActiveConversation] =
    useState<Conversation | null>(null);
  const [messages, setMessages] = useState<DMMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  const [composeEmployees, setComposeEmployees] = useState<
    ConversationMember[]
  >([]);
  const [composeConversationName, setComposeConversationName] = useState("");
  const [composeMessage, setComposeMessage] = useState("");
  const [composeError, setComposeError] = useState<string | null>(null);
  const [composeEmployeesError, setComposeEmployeesError] = useState<
    string | undefined
  >();
  const [composeMessageError, setComposeMessageError] = useState<
    string | undefined
  >();
  const [composeSubmitting, setComposeSubmitting] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const loadConversations = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await directMessagesService.listConversations({ limit: 100 });
      const sorted = [...(res.conversations || [])].sort((a, b) => {
        const aTime = new Date(a.lastMessageAt || 0).getTime();
        const bTime = new Date(b.lastMessageAt || 0).getTime();
        return bTime - aTime;
      });
      setConversations(sorted);
    } catch (err) {
      setListError(
        (err as Error)?.message ||
          t("frontdesk.internalChat.loadFailed", {
            defaultValue: "Failed to load conversations",
          }),
      );
    } finally {
      setListLoading(false);
    }
  }, [t]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const openThread = useCallback(
    async (conversation: Conversation) => {
      setActiveConversation(conversation);
      setView("thread");
      setThreadError(null);
      setDraft("");
      setMessages([]);
      setThreadLoading(true);
      setFocusConversationId(conversation.id);

      try {
        const res = await directMessagesService.listMessages(conversation.id, {
          limit: 80,
        });
        const list = [...(res.messages || [])].sort((a, b) => {
          const aTime = new Date(a.createdAt || 0).getTime();
          const bTime = new Date(b.createdAt || 0).getTime();
          return aTime - bTime;
        });
        setMessages(list);
        await directMessagesService
          .markAsRead(conversation.id, {})
          .catch(() => undefined);
        setConversations((prev) =>
          prev.map((c) =>
            c.id === conversation.id ? { ...c, unreadCount: 0 } : c,
          ),
        );
      } catch (err) {
        setThreadError(
          (err as Error)?.message ||
            t("frontdesk.internalChat.loadThreadFailed", {
              defaultValue: "Failed to load messages",
            }),
        );
      } finally {
        setThreadLoading(false);
        requestAnimationFrame(() => scrollToBottom("auto"));
      }
    },
    [scrollToBottom, setFocusConversationId, t],
  );

  // Keep conversation list (and unread) fresh for the floating launcher via HTTP
  useEffect(() => {
    if (!canShow) return;
    void loadConversations();

    // Poll while the panel is closed/minimized so unread pulse stays accurate
    // without relying on websocket.
    const shouldPoll = !open || minimized;
    if (!shouldPoll) return;

    const intervalId = window.setInterval(() => {
      void loadConversations();
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [canShow, open, minimized, loadConversations]);

  const openedThreadIdRef = useRef<string | null>(null);

  // Deep-link from toast / envelope with a conversation id
  useEffect(() => {
    if (!open || !focusConversationId || !canShow) return;
    if (openedThreadIdRef.current === focusConversationId) return;

    const existing = conversations.find((c) => c.id === focusConversationId);
    if (existing) {
      openedThreadIdRef.current = focusConversationId;
      void openThread(existing);
      return;
    }

    if (listLoading) return;

    void (async () => {
      try {
        const conv = await directMessagesService.getConversation(
          focusConversationId,
        );
        openedThreadIdRef.current = focusConversationId;
        setConversations((prev) => {
          if (prev.some((c) => c.id === conv.id)) return prev;
          return [conv, ...prev];
        });
        await openThread(conv);
      } catch {
        // Stay on list
      }
    })();
  }, [
    open,
    focusConversationId,
    canShow,
    conversations,
    listLoading,
    openThread,
  ]);

  // Poll messages while a thread is open (HTTP only — no DM websocket)
  useEffect(() => {
    if (!open || minimized || view !== "thread" || !activeConversation?.id) {
      return;
    }

    const conversationId = activeConversation.id;
    let cancelled = false;

    const refreshMessages = async () => {
      try {
        const res = await directMessagesService.listMessages(conversationId, {
          limit: 80,
        });
        if (cancelled) return;
        const list = [...(res.messages || [])].sort((a, b) => {
          const aTime = new Date(a.createdAt || 0).getTime();
          const bTime = new Date(b.createdAt || 0).getTime();
          return aTime - bTime;
        });
        let didUpdate = false;
        setMessages((prev) => {
          const prevLast = prev[prev.length - 1]?.id;
          const nextLast = list[list.length - 1]?.id;
          if (
            prev.length === list.length &&
            prevLast &&
            nextLast &&
            prevLast === nextLast
          ) {
            return prev;
          }
          didUpdate = true;
          return list;
        });
        if (didUpdate) {
          requestAnimationFrame(() => scrollToBottom("smooth"));
        }
        await directMessagesService
          .markAsRead(conversationId, {})
          .catch(() => undefined);
        setConversations((prev) =>
          prev.map((c) =>
            c.id === conversationId ? { ...c, unreadCount: 0 } : c,
          ),
        );
      } catch {
        // Keep last good snapshot
      }
    };

    const intervalId = window.setInterval(() => {
      void refreshMessages();
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    open,
    minimized,
    view,
    activeConversation?.id,
    scrollToBottom,
  ]);

  const filteredConversations = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => {
      const title = conversationTitle(c, currentUserId, t).toLowerCase();
      const preview = (c.lastMessage?.content || "").toLowerCase();
      return title.includes(q) || preview.includes(q);
    });
  }, [conversations, query, currentUserId, t]);

  const hasUnreadMessages = useMemo(
    () => conversations.some((c) => (c.unreadCount || 0) > 0),
    [conversations],
  );

  const handleMinimize = () => {
    setMinimized(true);
  };

  const handleRestore = () => {
    setMinimized(false);
    setOpen(true);
  };

  const handleBackToList = () => {
    setView("list");
    setActiveConversation(null);
    setFocusConversationId(null);
    openedThreadIdRef.current = null;
    setThreadError(null);
    void loadConversations();
  };

  const handleMarkConversationUnread = async (
    conversation: Conversation,
    event?: React.MouseEvent,
  ) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!conversation?.id) return;
    try {
      await directMessagesService.markAsUnread(conversation.id);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversation.id
            ? { ...c, unreadCount: Math.max(c.unreadCount || 0, 1) }
            : c,
        ),
      );
    } catch {
      // Keep previous unread state
    }
  };

  const handleSend = async (
    content: string,
    attachments: InternalPendingAttachment[],
  ) => {
    if (!activeConversation?.id || sending) return;
    if (!content.trim() && attachments.length === 0) return;

    setSending(true);
    setThreadError(null);
    try {
      const metadata =
        attachments.length > 0
          ? {
              type:
                attachments.length === 1
                  ? attachments[0].kind
                  : ("mixed" as const),
              attachments: attachments.map((a) => ({
                id: a.id,
                name: a.name,
                mimeType: a.mimeType,
                size: a.size,
                kind: a.kind,
                dataUrl: a.dataUrl,
              })),
            }
          : { type: "text" as const };

      const sent = await directMessagesService.sendMessage(
        activeConversation.id,
        {
          content:
            content.trim() ||
            (attachments.some((a) => a.kind === "voice")
              ? "🎤 Voice message"
              : attachments.some((a) => a.kind === "image")
                ? "📷 Image"
                : "📎 Attachment"),
          metadata,
        },
      );
      setMessages((prev) => {
        if (sent?.id && prev.some((m) => m.id === sent.id)) return prev;
        return [...prev, sent];
      });
      setDraft("");
      setConversations((prev) => {
        const updated = prev.map((c) =>
          c.id === activeConversation.id
            ? {
                ...c,
                lastMessageAt: sent.createdAt,
                lastMessage: {
                  id: sent.id,
                  content: sent.content,
                  userId: sent.userId,
                  createdAt: sent.createdAt,
                },
              }
            : c,
        );
        return updated.sort((a, b) => {
          const aTime = new Date(a.lastMessageAt || 0).getTime();
          const bTime = new Date(b.lastMessageAt || 0).getTime();
          return bTime - aTime;
        });
      });
      requestAnimationFrame(() => scrollToBottom("smooth"));
    } catch (err) {
      setThreadError(
        (err as Error)?.message ||
          t("frontdesk.directMessages.sendFailed", {
            defaultValue: "Failed to send message",
          }),
      );
    } finally {
      setSending(false);
    }
  };

  const handleComposeSubmit = async () => {
    setComposeError(null);
    let hasError = false;
    if (composeEmployees.length === 0) {
      setComposeEmployeesError(
        t("frontdesk.compose.selectEmployeeRequired", {
          defaultValue: "Select at least one employee",
        }),
      );
      hasError = true;
    } else {
      setComposeEmployeesError(undefined);
    }
    const text = composeMessage.trim();
    if (!text) {
      setComposeMessageError(
        t("frontdesk.compose.messageRequired", {
          defaultValue: "Message is required",
        }),
      );
      hasError = true;
    } else {
      setComposeMessageError(undefined);
    }
    if (hasError) return;

    setComposeSubmitting(true);
    try {
      const customName = composeConversationName.trim();
      const employeeNames = composeEmployees
        .map(memberDisplayName)
        .filter(Boolean)
        .join(", ");
      const created = await directMessagesService.createConversation({
        participantIds: composeEmployees.map((e) => e.userId),
        participants: composeEmployees.map((e) => ({
          userId: e.userId,
          email: e.email,
          firstName: e.firstName,
          lastName: e.lastName,
          name: memberDisplayName(e),
          employeeId: e.employeeId,
        })),
        // Custom name wins; otherwise always store full employee name(s)
        name: customName || employeeNames || undefined,
        message: text,
      });
      const conversationId =
        created.conversation?.id || (created as { id?: string }).id;
      setComposeEmployees([]);
      setComposeConversationName("");
      setComposeMessage("");
      await loadConversations();
      if (conversationId) {
        const fromList = (
          await directMessagesService.listConversations({ limit: 100 })
        ).conversations.find((c) => c.id === conversationId);
        if (fromList) {
          await openThread(fromList);
        } else if (created.conversation) {
          await openThread(created.conversation);
        }
      }
    } catch (err) {
      setComposeError(
        (err as Error)?.message ||
          t("frontdesk.compose.failedToCreateConversation", {
            defaultValue: "Failed to create conversation",
          }),
      );
    } finally {
      setComposeSubmitting(false);
    }
  };

  const handleOpenInFrontdesk = () => {
    const id = activeConversation?.id;
    const path = id
      ? buildRoutePath(RouteBuilder.internal(id))
      : buildRoutePath(RouteBuilder.chatwootDirectMessages());
    window.open(path, "_blank", "noopener,noreferrer");
  };

  if (!canShow) return null;

  const headerTitle =
    view === "compose"
      ? t("frontdesk.compose.newConversation", {
          defaultValue: "New conversation",
        })
      : view === "thread"
        ? conversationTitle(
            activeConversation || ({ members: [] } as Conversation),
            currentUserId,
            t,
          )
        : t("frontdesk.internalChat.title", {
            defaultValue: "Internal chats",
          });

  const anchorSx = isRTL
    ? { left: 16, right: "auto" }
    : { right: 16, left: "auto" };

  const showPanel = open && !minimized;

  if (!showPanel) {
    const minimizedAnchorSx = isRTL
      ? { left: 0, right: "auto" }
      : { right: 0, left: "auto" };

    return (
      <Box
        component="button"
        type="button"
        onClick={handleRestore}
        aria-label={t("frontdesk.internalChat.restore", {
          defaultValue: "Open internal chats",
        })}
        sx={{
          all: "unset",
          boxSizing: "border-box",
          position: "fixed",
          bottom: 85,
          ...minimizedAnchorSx,
          zIndex: 1_000_000,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 36,
          width: 20,
          maxWidth: "calc(100vw - 8px)",
          overflow: "hidden",
          borderRadius: isRTL ? "0 8px 8px 0" : "8px 0 0 8px",
          backgroundColor: BRAND,
          color: "#fff",
          boxShadow:
            "0 3px 10px rgba(15, 23, 42, 0.16), 0 1px 3px rgba(15, 23, 42, 0.08)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderLeft: isRTL ? "none" : undefined,
          borderRight: isRTL ? undefined : "none",
          direction: dir,
          transition:
            "width 180ms ease, height 180ms ease, padding 180ms ease, box-shadow 180ms ease, border-radius 180ms ease, filter 180ms ease",
          ...(hasUnreadMessages
            ? {
                backgroundColor: "#E11D48",
                borderColor: "rgba(255,255,255,0.35)",
                animation:
                  "internalChatUnreadPulse 2.4s ease-in-out infinite",
                "@keyframes internalChatUnreadPulse": {
                  "0%, 100%": {
                    backgroundColor: "#E11D48",
                    filter: "brightness(1)",
                    boxShadow:
                      "0 0 0 0 rgba(225, 29, 72, 0.75), 0 3px 10px rgba(15, 23, 42, 0.2)",
                  },
                  "50%": {
                    backgroundColor: "#FB7185",
                    filter: "brightness(1.15)",
                    boxShadow:
                      "0 0 0 8px rgba(225, 29, 72, 0), 0 4px 16px rgba(225, 29, 72, 0.45)",
                  },
                },
              }
            : null),
          "&:hover, &:focus-visible": {
            width: 176,
            height: 40,
            px: 1,
            justifyContent: "flex-start",
            gap: 0.75,
            borderRadius: isRTL ? "0 10px 10px 0" : "10px 0 0 10px",
            backgroundColor: hasUnreadMessages ? "#E11D48" : BRAND,
            boxShadow:
              "0 6px 16px rgba(15, 23, 42, 0.2), 0 2px 5px rgba(15, 23, 42, 0.1)",
            animation: "none",
            filter: "none",
            "& .internal-chat-minimized-label": {
              display: "block",
              opacity: 1,
              maxWidth: 130,
            },
            "& .internal-chat-minimized-icon": {
              animation: "none",
            },
          },
          "&:focus-visible": {
            outline: "2px solid #fff",
            outlineOffset: 2,
          },
        }}
      >
        <ChatIcon
          className="internal-chat-minimized-icon"
          sx={{
            fontSize: 15,
            flexShrink: 0,
            display: "block",
            lineHeight: 1,
            ...(hasUnreadMessages
              ? {
                  animation:
                    "internalChatIconBob 2.4s ease-in-out infinite",
                  "@keyframes internalChatIconBob": {
                    "0%, 100%": {
                      transform: "translateY(0) scale(1)",
                    },
                    "50%": {
                      transform: "translateY(-2px) scale(1.18)",
                    },
                  },
                }
              : null),
          }}
        />
        <Typography
          className="internal-chat-minimized-label"
          noWrap
          sx={{
            display: "none",
            fontWeight: 600,
            fontSize: "0.8125rem",
            lineHeight: 1,
            ...FONT_FAMILY.Lexend(600, 13),
            opacity: 0,
            maxWidth: 0,
            overflow: "hidden",
            transition: "opacity 160ms ease, max-width 180ms ease",
          }}
        >
          {t("frontdesk.internalChat.title", {
            defaultValue: "Internal chats",
          })}
        </Typography>
      </Box>
    );
  }

  return (
    <Fade in={showPanel}>
      <Paper
        elevation={8}
        role="dialog"
        aria-label={t("frontdesk.internalChat.title", {
          defaultValue: "Internal chats",
        })}
        sx={{
          position: "fixed",
          bottom: 16,
          ...anchorSx,
          width: { xs: "calc(100vw - 24px)", sm: PANEL_WIDTH },
          maxWidth: "calc(100vw - 24px)",
          height: { xs: "min(70vh, 560px)", sm: PANEL_HEIGHT },
          maxHeight: "calc(100vh - 88px)",
          zIndex: 1_000_000,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: "16px",
          direction: dir,
          boxShadow:
            "0 12px 40px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.08)",
          border: `1px solid ${colorTokens.neutral[200]}`,
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 1.5,
            py: view === "thread" ? 1.1 : 1.25,
            borderBottom: "none",
            backgroundColor: BRAND,
            color: "#fff",
            direction: dir,
            minHeight: 56,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, textAlign: "start" }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                ...FONT_FAMILY.Lexend(600, 16),
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "start",
              }}
            >
              {headerTitle}
            </Typography>
          </Box>
          {view === "list" && (
            <Tooltip
              title={t("frontdesk.compose.newConversation", {
                defaultValue: "New conversation",
              })}
            >
              <IconButton
                size="small"
                onClick={() => {
                  setView("compose");
                  setComposeError(null);
                  setComposeEmployeesError(undefined);
                  setComposeMessageError(undefined);
                  setComposeConversationName("");
                }}
                aria-label={t("frontdesk.compose.newConversation", {
                  defaultValue: "New conversation",
                })}
                sx={{
                  color: "#fff",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
          {(view === "thread" || view === "compose") && (
            <Tooltip
              title={t("frontdesk.internalChat.allConversations", {
                defaultValue: "All conversations",
              })}
            >
              <IconButton
                size="small"
                onClick={
                  view === "compose" ? () => setView("list") : handleBackToList
                }
                aria-label={t("frontdesk.internalChat.allConversations", {
                  defaultValue: "All conversations",
                })}
                sx={{ color: "#fff" }}
              >
                <ForumOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip
            title={t("frontdesk.internalChat.openFull", {
              defaultValue: "Open in FrontDesk",
            })}
          >
            <IconButton
              size="small"
              onClick={handleOpenInFrontdesk}
              aria-label={t("frontdesk.internalChat.openFull", {
                defaultValue: "Open in FrontDesk",
              })}
              sx={{ color: "#fff" }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={t("frontdesk.internalChat.minimize", {
              defaultValue: "Minimize",
            })}
          >
            <IconButton
              size="small"
              onClick={handleMinimize}
              aria-label={t("frontdesk.internalChat.minimize", {
                defaultValue: "Minimize",
              })}
              sx={{ color: "#fff" }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Body */}
        {view === "list" && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              backgroundColor: colorTokens.semantic.background.primary,
            }}
          >
            <Box sx={{ px: 1.5, py: 1.25, direction: dir }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 6,
                  backgroundColor: colorTokens.neutral[100],
                  border: `1px solid ${colorTokens.neutral[200]}`,
                  direction: dir,
                }}
              >
                <SearchIcon
                  sx={{ fontSize: 18, color: colorTokens.neutral[500] }}
                />
                <InputBase
                  fullWidth
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("frontdesk.internalChat.search", {
                    defaultValue: "Search or start a new chat...",
                  })}
                  inputProps={{ dir, style: { textAlign: "start" } }}
                  sx={{
                    fontSize: "0.875rem",
                    // Outranks the modern theme's `.MuiInputBase-input { height: 40px !important }`.
                    "& .MuiInputBase-input.MuiInputBase-input": {
                      height: "auto !important",
                      minHeight: "0 !important",
                      padding: "0 !important",
                      lineHeight: "1.5 !important",
                    },
                  }}
                />
              </Box>
            </Box>

            {listError && (
              <Alert
                severity="error"
                sx={{ mx: 1.5, mb: 1 }}
                action={
                  <Button color="inherit" size="small" onClick={loadConversations}>
                    {t("frontdesk.notifications.retry", {
                      defaultValue: "Retry",
                    })}
                  </Button>
                }
              >
                {listError}
              </Alert>
            )}

            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              {listLoading && conversations.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 6,
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              ) : filteredConversations.length === 0 ? (
                <Box sx={{ px: 3, py: 6, textAlign: "center" }}>
                  <Typography
                    sx={{
                      color: neutralColor(600),
                      fontSize: "0.875rem",
                      mb: 2,
                    }}
                  >
                    {t("frontdesk.internalChat.empty", {
                      defaultValue: "No internal conversations yet",
                    })}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setComposeConversationName("");
                      setView("compose");
                    }}
                    sx={{ textTransform: "none" }}
                  >
                    {t("frontdesk.compose.newConversation", {
                      defaultValue: "New conversation",
                    })}
                  </Button>
                </Box>
              ) : (
                <List dense disablePadding>
                  {filteredConversations.map((conversation) => {
                    const title = conversationTitle(
                      conversation,
                      currentUserId,
                      t,
                    );
                    const avatarName = conversationAvatarName(
                      conversation,
                      currentUserId,
                      t,
                    );
                    const unread = conversation.unreadCount || 0;
                    const selected = activeConversation?.id === conversation.id;
                    const preview = formatInternalChatPreview(
                      conversation.lastMessage?.content || "",
                      t,
                    );
                    const voice = isVoicePreview(
                      conversation.lastMessage?.content || "",
                    );
                    return (
                      <ListItemButton
                        key={conversation.id}
                        selected={selected}
                        onClick={() => void openThread(conversation)}
                        sx={{
                          gap: 0.75,
                          py: 1,
                          px: 1.25,
                          alignItems: "center",
                          borderBottom: `1px solid ${colorTokens.neutral[100]}`,
                          borderInlineStart: selected
                            ? `4px solid ${BRAND}`
                            : "4px solid transparent",
                          backgroundColor: selected
                            ? SELECTED_ROW_BG
                            : "transparent",
                          "&:hover": {
                            backgroundColor: selected
                              ? SELECTED_ROW_BG
                              : colorTokens.neutral[50],
                          },
                          "&.Mui-selected": {
                            backgroundColor: SELECTED_ROW_BG,
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: SELECTED_ROW_BG,
                          },
                        }}
                      >
                        <Badge
                          color="error"
                          badgeContent={unread > 0 ? unread : 0}
                          invisible={unread <= 0}
                          sx={{ flexShrink: 0 }}
                        >
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: "#EEF0FF",
                              color: BRAND,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                            }}
                          >
                            {initials(avatarName)}
                          </Avatar>
                        </Badge>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: unread > 0 ? 700 : 600,
                              fontSize: "0.9rem",
                              color: colorTokens.neutral[900],
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textAlign: "start",
                              lineHeight: 1.3,
                            }}
                          >
                            {title}
                          </Typography>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                            sx={{ mt: 0.25, minWidth: 0 }}
                          >
                            {voice && (
                              <MicIcon
                                sx={{
                                  fontSize: 14,
                                  color: colorTokens.neutral[500],
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                color: colorTokens.neutral[500],
                                fontWeight: unread > 0 ? 600 : 400,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textAlign: "start",
                                lineHeight: 1.3,
                              }}
                            >
                              {voice
                                ? t("frontdesk.compose.voiceMessage", {
                                    defaultValue: "Voice message",
                                  })
                                : preview ||
                                  t("frontdesk.internalChat.noMessages", {
                                    defaultValue: "No messages yet",
                                  })}
                            </Typography>
                          </Stack>
                        </Box>
                        <Stack
                          alignItems="center"
                          spacing={0.15}
                          sx={{ flexShrink: 0, minWidth: 36 }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color:
                                unread > 0
                                  ? BRAND
                                  : colorTokens.neutral[500],
                              fontWeight: unread > 0 ? 600 : 400,
                              lineHeight: 1.2,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatListTime(
                              conversation.lastMessageAt ||
                                conversation.lastMessage?.createdAt,
                            )}
                          </Typography>
                          {unread <= 0 && (
                            <Tooltip
                              title={t("frontdesk.internalChat.markAsUnread", {
                                defaultValue: "Mark as unread",
                              })}
                            >
                              <IconButton
                                className="mark-unread-btn"
                                size="small"
                                aria-label={t(
                                  "frontdesk.internalChat.markAsUnread",
                                  { defaultValue: "Mark as unread" },
                                )}
                                onClick={(event) =>
                                  void handleMarkConversationUnread(
                                    conversation,
                                    event,
                                  )
                                }
                                sx={{
                                  opacity: 0.55,
                                  color: colorTokens.neutral[500],
                                  p: 0.15,
                                  "&:hover": {
                                    opacity: 1,
                                    color: BRAND,
                                    backgroundColor:
                                      "rgba(46, 48, 146, 0.08)",
                                  },
                                }}
                              >
                                <MarkEmailUnreadOutlinedIcon
                                  sx={{ fontSize: 15 }}
                                />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>
        )}

        {view === "thread" && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#F4F5F9",
            }}
          >
            {threadError && (
              <Alert
                severity="error"
                onClose={() => setThreadError(null)}
                sx={{ m: 1 }}
              >
                {threadError}
              </Alert>
            )}
            <Box
              ref={scrollRef}
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                px: 1.5,
                py: 1.5,
                backgroundImage: CHAT_PATTERN_BG,
                backgroundColor: "#F4F5F9",
              }}
            >
              {threadLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 6,
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              ) : messages.length === 0 ? (
                <Typography
                  sx={{
                    textAlign: "center",
                    color: colorTokens.neutral[600],
                    fontSize: "0.875rem",
                    py: 4,
                  }}
                >
                  {t("frontdesk.internalChat.noMessages", {
                    defaultValue: "No messages yet",
                  })}
                </Typography>
              ) : (
                <Stack spacing={0.85}>
                  {messages.map((message, index) => {
                    const isOwn =
                      Boolean(currentUserId) &&
                      message.userId === currentUserId;
                    const prev = messages[index - 1];
                    const showDay =
                      !prev ||
                      dayKey(prev.createdAt) !== dayKey(message.createdAt);
                    return (
                      <React.Fragment
                        key={message.id || message.tempId || message.createdAt}
                      >
                        {showDay && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              my: 0.75,
                            }}
                          >
                            <Box
                              sx={{
                                px: 1.5,
                                py: 0.4,
                                borderRadius: 5,
                                backgroundColor: "rgba(255,255,255,0.92)",
                                border: `1px solid ${colorTokens.neutral[200]}`,
                                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.7rem",
                                  color: colorTokens.neutral[600],
                                  fontWeight: 600,
                                }}
                              >
                                {formatDayLabel(message.createdAt)}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: isOwn
                              ? "flex-end"
                              : "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: "82%",
                              px: 1.35,
                              py: 0.85,
                              borderRadius: isOwn
                                ? "14px 14px 4px 14px"
                                : "14px 14px 14px 4px",
                              bgcolor: isOwn ? OUTGOING_BUBBLE : "#fff",
                              color: colorTokens.neutral[900],
                              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08)",
                              border: isOwn
                                ? "none"
                                : `1px solid ${colorTokens.neutral[200]}`,
                            }}
                          >
                            {!isOwn && (
                              <Typography
                                sx={{
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  color: BRAND,
                                  mb: 0.25,
                                  textAlign: "start",
                                }}
                              >
                                {message.userName ||
                                  message.user?.name ||
                                  message.userEmail ||
                                  "Employee"}
                              </Typography>
                            )}
                            {(() => {
                              const attachments =
                                getInternalMessageAttachments(message);
                              const quoteApproval =
                                getQuoteApprovalMeta(message);
                              const quoteResult =
                                getQuoteApprovalResultMeta(message);
                              const showText =
                                Boolean(message.content?.trim()) &&
                                !quoteApproval &&
                                !quoteResult &&
                                !(
                                  attachments.length > 0 &&
                                  isInternalMediaPlaceholder(message.content)
                                );
                              return (
                                <>
                                  {showText && (
                                    <Typography
                                      sx={{
                                        fontSize: "0.875rem",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        textAlign: "start",
                                        lineHeight: 1.45,
                                      }}
                                    >
                                      {message.content}
                                    </Typography>
                                  )}
                                  {quoteResult && (
                                    <QuoteApprovalResultMessage
                                      data={quoteResult}
                                      content={message.content}
                                    />
                                  )}
                                  {quoteApproval && (
                                    <QuoteApprovalMessageCard
                                      data={quoteApproval}
                                      compact
                                    />
                                  )}
                                  <InternalMessageAttachments
                                    attachments={attachments}
                                  />
                                </>
                              );
                            })()}
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="flex-end"
                              spacing={0.4}
                              sx={{ mt: 0.4 }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.65rem",
                                  color: colorTokens.neutral[500],
                                }}
                              >
                                {formatMsgTime(message.createdAt)}
                              </Typography>
                              {isOwn && (
                                <DoneAllIcon
                                  sx={{ fontSize: 14, color: BRAND }}
                                />
                              )}
                            </Stack>
                          </Box>
                        </Box>
                      </React.Fragment>
                    );
                  })}
                </Stack>
              )}
            </Box>
            <Box
              sx={{
                borderTop: `1px solid ${colorTokens.neutral[200]}`,
                backgroundColor: "#fff",
                px: 0.75,
                py: 1,
                // Keep a bit of inset on the text-field side; sit send nearer the edge
                ...(dir === "rtl"
                  ? { pl: 0.5, pr: 1 }
                  : { pr: 0.5, pl: 1 }),
              }}
            >
              <InternalReplyComposer
                variant="popup"
                value={draft}
                onChange={setDraft}
                onSend={handleSend}
                disabled={threadLoading}
                isSending={sending}
              />
            </Box>
          </Box>
        )}

        {view === "compose" && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: colorTokens.semantic.background.primary,
            }}
          >
            {composeError && (
              <Alert
                severity="error"
                onClose={() => setComposeError(null)}
                sx={{ m: 1.5, flexShrink: 0 }}
              >
                {composeError}
              </Alert>
            )}

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                px: 1.5,
                pt: 1,
                direction: dir,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  mb: 0.5,
                  color: colorTokens.neutral[700],
                  textAlign: "start",
                  flexShrink: 0,
                }}
              >
                {t("frontdesk.compose.conversationName", {
                  defaultValue: "Conversation name",
                })}{" "}
                <Box
                  component="span"
                  sx={{
                    color: colorTokens.neutral[500],
                    fontWeight: 500,
                    textTransform: "none",
                    letterSpacing: 0,
                    fontSize: "0.65rem",
                  }}
                >
                  (
                  {t("frontdesk.compose.optional", {
                    defaultValue: "optional",
                  })}
                  )
                </Box>
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={composeConversationName}
                onChange={(e) => setComposeConversationName(e.target.value)}
                placeholder={t(
                  "frontdesk.compose.conversationNamePlaceholder",
                  {
                    defaultValue: "e.g. Project updates",
                  },
                )}
                disabled={composeSubmitting}
                inputProps={{ dir, style: { textAlign: "start" } }}
                sx={{
                  mb: 1,
                  flexShrink: 0,
                  "& .MuiInputBase-root": { minHeight: 32 },
                  "& .MuiInputBase-input": {
                    py: 0.6,
                    fontSize: "0.8125rem",
                  },
                }}
              />
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <InternalEmployeePicker
                  embedded
                  fillHeight
                  selected={composeEmployees}
                  onChange={(users) => {
                    setComposeEmployees(users);
                    setComposeEmployeesError(undefined);
                  }}
                  error={composeEmployeesError}
                />
              </Box>
            </Box>

            {/* Message + send stay fixed below the list */}
            <Box
              sx={{
                flexShrink: 0,
                px: 1.5,
                pt: 1,
                pb: 0.5,
                direction: dir,
                borderTop: `1px solid ${colorTokens.neutral[200]}`,
                backgroundColor: colorTokens.semantic.background.primary,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  mb: 0.5,
                  color: composeMessageError
                    ? "#DC2626"
                    : colorTokens.neutral[700],
                  textAlign: "start",
                }}
              >
                {t("frontdesk.compose.message")}{" "}
                <Box component="span" sx={{ color: "#DC2626" }}>
                  *
                </Box>
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={1}
                maxRows={3}
                value={composeMessage}
                onChange={(e) => {
                  setComposeMessage(e.target.value);
                  if (e.target.value.trim()) {
                    setComposeMessageError(undefined);
                  }
                }}
                error={Boolean(composeMessageError)}
                helperText={composeMessageError}
                placeholder={t(
                  "frontdesk.compose.writeMessagePlaceholder",
                  { defaultValue: "Write your message..." },
                )}
                disabled={composeSubmitting}
                inputProps={{ dir, style: { textAlign: "start" } }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.8125rem",
                    py: 0.75,
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                flexShrink: 0,
                px: 1.5,
                py: 1,
                // Force LTR so flex-start/end map to physical left/right
                direction: "ltr",
                display: "flex",
                justifyContent: isRTL ? "flex-start" : "flex-end",
              }}
            >
              <Button
                variant="contained"
                onClick={() => void handleComposeSubmit()}
                disabled={composeSubmitting}
                startIcon={
                  composeSubmitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SendIcon
                      sx={{ transform: isRTL ? "scaleX(-1)" : "none" }}
                    />
                  )
                }
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {t("frontdesk.compose.sendMessage", {
                  defaultValue: "Send message",
                })}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Fade>
  );
};
